/* SHARED D&A COMPUTE ENGINE */
import { addMonths } from './dateUtils';
import { daStartOffsetMonths } from '../utils/daStartConvention';

const r2 = n => Math.round(n * 100) / 100;

// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.

/* ★ B5 K3 — A MISSING DATE HAS NO MONTH.
 * This used to return "2026-01", which dated every dateless asset to January 2026 —
 * and did so CONSISTENTLY in both consumers, so the asset depreciated, its cost landed,
 * and everything tied. A self-consistent wrong answer is invisible; that is what made it
 * worth removing rather than merely wrong. Returning null forces each caller to state
 * what it does about a missing date instead of inheriting an invented one.
 * ⛔ EVERY CALLER MUST HANDLE THE NULL. Two in this file (assetMonthlySchedule and
 * routeAsset, both below) and three on the D&A page, which now imports this function
 * rather than keeping its own copy. */
function toMonth(d) {
  if (!d) return null;
  const s = String(d);
  const parts = s.split("-");
  return parts[0] + "-" + parts[1];
}

/* THE D&A START CONVENTION -- THE ONE PLACE IT IS APPLIED (2026-10-13).
 * A company either starts an asset's schedule in the month it is placed in service ('in_month' -- the default, and what
 * every company did before this existed) or the month AFTER ('next_month'). The vocabulary and the stored value have one
 * owner, utils/daStartConvention.js; this function is the only code that turns the convention into a month.
 *   - absent / null / unknown convention -> offset 0 -> the month is returned exactly as toMonth produced it (not even
 *     re-formatted), so a company that never chose is byte-identical to before.
 *   - it moves the SCHEDULE's first month only. The life is not shortened: assetMonthlySchedule still emits every monthly
 *     amount, one month later. The COST is not moved: routeAsset (below) keys grossAdditions by toMonth(a.date), the
 *     purchase month, under either convention -- the forecast balance sheet rolls gross assets forward from the close
 *     balance, so moving the cost would double-book a purchase made in the close month.
 * Every reader of a schedule's start month calls this (assetMonthlySchedule here; the D&A page's asset editor), so there
 * is no second copy of the "+1". */
function depreciationStartMonth(date, startConvention) {
  const m = toMonth(date);
  if (!m) return null;
  const offset = daStartOffsetMonths(startConvention);
  return offset ? addMonths(m, offset) : m;
}

function assetMonthlySchedule(asset, startConvention) {
  const life = asset.life;
  const cost = asset.cost;
  const salvage = asset.salvage || 0;
  const depBase = cost - salvage;
  const totalMonths = Math.round(life * 12);
  const method = asset.method || "SL";
  const startM = depreciationStartMonth(asset.date, startConvention);   // the schedule's first month (the convention is applied there)
  if (!startM) {
    // ★ B5 K3 — no date, no schedule. Skip LOUDLY, exactly as routeAsset does below for a
    // missing bs_line_id: an empty `monthly` contributes 0 to every month by construction,
    // so the asset is honestly ABSENT rather than present at an invented month.
    // ⛔ WARN, NEVER THROW — computeDA runs inside the render path via runFullModel, so a
    // throw here is a blank page, not a message anyone reads.
    console.warn('[computeDA] asset has no purchase date — no depreciation/amortization schedule (skipped, no date fallback):',
      { id: asset.id, desc: asset.desc, cost: asset.cost });
    return { startM: null, monthly: [] };
  }
  const monthly = [];

  if (method === "SL") {
    const mo = depBase / totalMonths;
    for (let i = 0; i < totalMonths; i++) monthly.push(mo);
  } else if (method === "DDB") {
    const annualRate = 2 / life;
    let bookValue = depBase;
    for (let yr = 0; yr < life; yr++) {
      const remainingYears = life - yr;
      const ddbAnnual = bookValue * annualRate;
      const slAnnual = bookValue / remainingYears;
      const annualDep = Math.min(Math.max(ddbAnnual, slAnnual), bookValue);
      const moAmt = annualDep / 12;
      for (let m = 0; m < 12; m++) monthly.push(moAmt);
      bookValue = bookValue - annualDep;
      if (bookValue <= 0.005) break;
    }
  } else if (method === "SYD") {
    const syd = (life * (life + 1)) / 2;
    for (let yr = 1; yr <= life; yr++) {
      const fraction = (life - yr + 1) / syd;
      const annualDep = depBase * fraction;
      const moAmt = annualDep / 12;
      for (let m = 0; m < 12; m++) monthly.push(moAmt);
    }
  }

  return { startM, monthly };
}

export function computeSchedule(assets, allMonths, startConvention) {
  const MONTHS = allMonths || [];
  const schedules = assets.map(a => assetMonthlySchedule(a, startConvention));
  const data = {};
  MONTHS.forEach(m => {
    let rawTotal = 0;
    schedules.forEach(({ startM, monthly }) => {
      // ★★ B5 K3 — (b) AND THIS ARE ONE CHANGE. Without this guard, `startM.split` on a
      // null throws an uncaught TypeError inside the render path, turning a silent
      // wrong-date into a blank page — strictly worse than the bug being fixed.
      // (b) has already warned about this asset; here we simply do not book it.
      if (!startM) return;
      const [my, mm] = m.split("-").map(Number);
      const [sy, sm] = startM.split("-").map(Number);
      const idx = (my - sy) * 12 + (mm - sm);
      if (idx >= 0 && idx < monthly.length) rawTotal += monthly[idx];
    });
    data[m] = { expense: r2(rawTotal) };
  });
  let accum = 0;
  MONTHS.forEach(m => {
    accum = r2(accum + data[m].expense);
    data[m].accumulated = accum;
  });
  return data;
}

/* ★ B5 s3a — THE STAMP-vs-POSITION ASSERTION (scaffolding; changes no number).
 *
 * Depreciation-vs-amortization is decided BY ARGUMENT POSITION: whatever arrives in
 * `fixedAssets` depreciates, whatever arrives in `intangAssets` amortizes. That is an
 * IMPLICIT contract — nothing in the data says which an asset is, so nothing can
 * disagree with the call site and nothing can report it if it did.
 *
 * ModelProvider now STAMPS `sub_type` on each asset from the endpoint it was fetched
 * from. This asserts the two agree, on every asset, on every run — so by the time s3b
 * makes the split READ `sub_type`, the field has already been proven to reproduce the
 * positional decision on live data rather than merely being assumed to.
 *
 * ⛔ WARN, NEVER THROW. computeDA runs inside the render path via runFullModel, so a
 *   throw blanks the statements — a worse failure than the one being reported. Same
 *   posture as buildBSLines' unbucketed-leaf warn and buildISLines' orphan warn.
 * ⓘ An asset with NO sub_type is skipped, not flagged: a caller that predates the stamp
 *   (a scenario fixture, a test harness) is not making a false claim about itself. Only
 *   a stamp that CONTRADICTS its array is a finding. */
function assertSubTypeMatchesPosition(fixedAssets, intangAssets) {
  const bad = [];
  const check = (arr, expected, arrName) => (arr || []).forEach(a => {
    if (a && a.sub_type && a.sub_type !== expected) {
      bad.push('id=' + a.id + ' sub_type=' + a.sub_type + ' but passed in ' + arrName);
    }
  });
  check(fixedAssets, 'fixed_assets', 'fixedAssets (depreciates)');
  check(intangAssets, 'intangible_assets', 'intangAssets (amortizes)');
  if (bad.length) {
    console.warn(
      '[computeDA] ' + bad.length + ' asset(s) carry a sub_type that CONTRADICTS the array ' +
      'they were passed in. Depreciation-vs-amortization is decided by the array, so each ' +
      'of these is being booked to the opposite IS line from the one its sub_type names. ' +
      'Offenders: ' + bad.join('; ')
    );
  }
}

/* ★★ B5 s3b — THE SWITCH. Depreciation-vs-amortization is decided by the asset's OWN
 * `sub_type`, not by which argument slot it arrived in.
 *
 * BEFORE: `computeSchedule(fixedAssets)` depreciated and `computeSchedule(intangAssets)`
 * amortized — an IMPLICIT contract carried by call position, which nothing in the data
 * could agree or disagree with. NOW the field decides, and position is only checked.
 *
 * ⓘ THE SIGNATURE DELIBERATELY DID NOT MOVE. computeDA still takes the two arrays, so
 *   runFullModel is untouched — and, more importantly, KEEPING them is what lets
 *   assertSubTypeMatchesPosition go on comparing the field against the slot. Collapsing
 *   to one list would have destroyed the very cross-check that de-risked this switch.
 *   The two arrays are now an INPUT GROUPING plus a redundancy check, not the decision.
 *
 * ⛔ AN ASSET WITH NO (OR AN UNKNOWN) sub_type IS NAMED AND SKIPPED, NEVER DEFAULTED.
 *   Picking a bucket for it would be a guess about an accounting treatment — the §E1
 *   defect this arc exists to remove — and it would be invisible: the asset would simply
 *   depreciate (or amortize) as if someone had decided. Skipping is visible in the
 *   totals and the warn says exactly which asset and why. Same posture as routeAsset's
 *   missing-bs_line_id path below.
 *   ⚠ KNOWN PRODUCER, CLOSED IN THIS COMMIT: ScenariosPage builds scenario-added assets
 *     as object literals; before s3b they carried no sub_type. They are stamped now. */
export function partitionBySubType(fixedAssets, intangAssets) {
  const depreciating = [], amortizing = [], unclassified = [];
  for (const a of [...(fixedAssets || []), ...(intangAssets || [])]) {
    if (!a) continue;
    if (a.sub_type === 'fixed_assets') depreciating.push(a);
    else if (a.sub_type === 'intangible_assets') amortizing.push(a);
    else unclassified.push(a);
  }
  if (unclassified.length) {
    console.warn(
      '[computeDA] ' + unclassified.length + ' asset(s) carry no recognised sub_type, so they ' +
      'are NEITHER depreciated NOR amortized — their D&A is absent from the income statement ' +
      'and the accumulated balances. sub_type is stamped from the endpoint an asset is ' +
      'fetched from; an asset built in code must set it too. Offenders: ' +
      unclassified.map(a => 'id=' + a.id + ' sub_type=' + JSON.stringify(a.sub_type)).join('; ')
    );
  }
  return { depreciating, amortizing };
}

export function computeDA(fixedAssets, intangAssets, allMonths, bsLines, startConvention) {
  const MONTHS = allMonths || [];
  // The cross-check survives the switch: it now guards the field the split READS.
  assertSubTypeMatchesPosition(fixedAssets, intangAssets);
  const { depreciating, amortizing } = partitionBySubType(fixedAssets, intangAssets);
  // startConvention (utils/daStartConvention.js): 'next_month' starts each schedule one month after the in-service month;
  // anything else starts it in that month. It reaches the two schedules ONLY -- never the gross additions below.
  const dep = computeSchedule(depreciating, MONTHS, startConvention);
  const amort = computeSchedule(amortizing, MONTHS, startConvention);
  const isLines = { other_depreciation: {}, other_amortization: {} };

  // Cp2 — per-asset BS-line routing. Every asset carries an EXPLICIT bs_line_id (DB column,
  // NOT NULL + server-validated to a BS leaf of the correct role). grossAdditions is keyed by
  // that stored line for BOTH fixed and intangible assets — ONE path, no isFixed routing branch,
  // no guessing. (Deleted: resolveBsKey, HARDCODED_TYPEMAP, the fixedDefault bucketing, and the
  // asset_type_match substring matcher — all replaced by the stored reference.) The key set is
  // the tenant's BS asset leaves (role fixed_asset OR intangible_asset); the CFS "(Purchase of) X"
  // lines derive from these BS lines automatically, so a correctly-routed asset needs no CFS change.
  const assetLeafIds = (bsLines || [])
    .filter(l => l.role === 'fixed_asset' || l.role === 'intangible_asset')
    .map(l => l.id);
  const validKey = new Set(assetLeafIds);
  const grossAdditions = {};
  assetLeafIds.forEach(k => { grossAdditions[k] = {}; MONTHS.forEach(m => { grossAdditions[k][m] = 0; }); });

  const routeAsset = (a) => {
    const key = a.bs_line_id;
    if (!key || !validKey.has(key)) {
      // bs_line_id is NOT NULL + validated server-side, so this should never fire. Skip LOUDLY
      // rather than silently bucket to a default — a silent default is exactly the guess we deleted.
      console.warn('[computeDA] asset has missing/unknown bs_line_id — skipped (no routing fallback):', { id: a.id, type: a.type, bs_line_id: key });
      return;
    }
    // The PURCHASE month -- deliberately toMonth, never depreciationStartMonth: the cost lands when the asset is bought,
    // under either D&A start convention.
    const m = toMonth(a.date);
    if (!m) {
      // ★ B5 K3 — mirrors the bs_line_id arm directly above: a missing date means we do not
      // know WHEN the purchase lands, so it is not booked at all rather than booked to an
      // invented month. Without this the null would key grossAdditions[key][null], which the
      // `!== undefined` test below skips SILENTLY — safe, but mute.
      console.warn('[computeDA] asset has no purchase date — gross addition skipped (no date fallback):',
        { id: a.id, bs_line_id: key, cost: a.cost });
      return;
    }
    if (grossAdditions[key][m] !== undefined) {
      grossAdditions[key][m] = r2(grossAdditions[key][m] + (a.cost || 0));
    }
  };
  fixedAssets.forEach(routeAsset);
  intangAssets.forEach(routeAsset);

  MONTHS.forEach(m => {
    isLines.other_depreciation[m] = r2(dep[m]?.expense || 0);
    isLines.other_amortization[m] = r2(amort[m]?.expense || 0);
  });
  return { dep, amort, isLines, grossAdditions };
}

export { r2, toMonth, assetMonthlySchedule, depreciationStartMonth };
